/**
 * Meals data access. Everything goes through `api` from src/lib/api.ts —
 * including the photo upload, which uses api.upload() for multipart. No raw fetch.
 *
 * Write shapes (MealInput, IngredientInput, GroceryItemInput) live in
 * src/shared/types.ts — one definition, shared with the Worker.
 */
import { api, ApiRequestError } from '../../lib/api';
import type {
  Aisle,
  GroceryItem,
  GroceryItemInput,
  GroceryList,
  MealPlanEntry,
  ParsedRecipe,
  Recipe,
  RecipeInput,
  SuggestedIngredientList,
} from '../../shared/types';

export type Slot = MealPlanEntry['slot'];

/**
 * What the meal editor saves. RecipeInput plus the imported-from link, which the
 * recipe view renders as "source". source_url rides alongside RecipeInput rather
 * than inside it because a hand-written recipe has no source — it stays null.
 */
export type SaveRecipeInput = RecipeInput & { source_url: string | null };

export const mealsApi = {
  // library
  listMeals: () => api.get<Recipe[]>('/meals'),
  createMeal: (input: SaveRecipeInput) => api.post<Recipe>('/meals', input),
  updateMeal: (id: number, input: SaveRecipeInput) => api.put<Recipe>(`/meals/${id}`, input),
  deleteMeal: (id: number) => api.del<Recipe>(`/meals/${id}`),
  removePhoto: (id: number) => api.del<Recipe>(`/meals/${id}/photo`),
  importRecipe: (url: string) => api.post<ParsedRecipe>('/meals/import', { url }),
  /**
   * Ingredients for a meal that has none, proposed from its name. Returns a draft to
   * review in the editor — it writes nothing, so the meal is unchanged until Save.
   */
  suggestIngredients: (body: {
    name: string;
    servings: number | null;
    notes: string | null;
    instructions: string | null;
  }) => api.post<SuggestedIngredientList>('/meals/suggest-ingredients', body),

  // plan
  getPlan: (weekStart: string) => api.get<MealPlanEntry[]>(`/meals/plan?week_start=${weekStart}`),
  setPlan: (body: { plan_date: string; slot: Slot; meal_id: number }) =>
    api.put<MealPlanEntry>('/meals/plan', body),
  clearPlan: (id: number) => api.del<{ ok: true }>(`/meals/plan/${id}`),

  // grocery
  getGrocery: (weekStart: string) => api.get<GroceryList>(`/meals/grocery?week_start=${weekStart}`),
  regenerate: (weekStart: string) =>
    api.post<GroceryList>(`/meals/grocery/regenerate?week_start=${weekStart}`),
  addGroceryItem: (body: GroceryItemInput) => api.post<GroceryItem>('/meals/grocery', body),
  updateGroceryItem: (id: number, body: Partial<Omit<GroceryItem, 'id' | 'week_start' | 'manual'>>) =>
    api.put<GroceryItem>(`/meals/grocery/${id}`, body),
  removeGroceryItem: (id: number) => api.del<{ ok: true }>(`/meals/grocery/${id}`),

  // aisle order (editable config, never hardcoded)
  listAisles: () => api.get<Aisle[]>('/meals/aisles'),
  createAisle: (body: { name: string }) => api.post<Aisle>('/meals/aisles', body),
  updateAisle: (id: number, body: { name?: string; sort_order?: number; active?: boolean }) =>
    api.put<Aisle>(`/meals/aisles/${id}`, body),
  deleteAisle: (id: number) => api.del<Aisle>(`/meals/aisles/${id}`),
};

/** Multipart upload to R2, via the Worker. Returns the meal with its new photo. */
export function uploadMealPhoto(id: number, file: File): Promise<Recipe> {
  const form = new FormData();
  form.append('photo', file);
  return api.upload<Recipe>(`/meals/${id}/photo`, form);
}

/** Human-readable message for anything thrown by the calls above. */
export function errMsg(err: unknown): string {
  if (err instanceof ApiRequestError) return err.body.detail ?? err.body.error;
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong.';
}

/** "2 lb", "1.5 cups", "3", or '' when an item has neither. */
export function qtyLabel(quantity: number | null, unit: string | null): string {
  const q = quantity === null ? '' : String(Math.round(quantity * 100) / 100);
  return [q, unit ?? ''].filter(Boolean).join(' ');
}

/** "allrecipes.com" from a full URL, for a tidy source link. Falls back to the raw url. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
